import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ModalCloseX from "../../components/common/ModalCloseX";
import { useAuth } from "../../context/AuthContext";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import useHelpGuideNavigation from "../../hooks/useHelpGuideNavigation";
import { getCurrentSalonId, getCurrentSalonName } from "../../utils/currentSalon";
import { formatDateTimeInTimeZone } from "../../utils/datetime";
import { getReportsHomePathForRoute } from "../../utils/reportRouteContext";
import "../ReportsSectionCompact.css";
import {
  getEmailSubscriptionSummary,
  getEmailSubscriptionTrends,
  getEmailSubscriptionReasons,
  getEmailSubscriptionPreferences,
  getEmailSubscriptionBatches,
  exportEmailOptOutClients,
  searchEmailOptOutClients,
} from "../../api/marketingEmailOptIn";
import { REPORT_HELP } from "../../constants/reportHelpContent";

const PREFERENCE_OPTIONS = [
  { value: "", label: "All preferences" },
  { value: "all_optional", label: "All optional emails" },
  { value: "feedback", label: "Feedback requests" },
  { value: "appointment_reminders", label: "Appointment reminders" },
  { value: "service_reminders", label: "Refill and service reminders" },
  { value: "promotions", label: "Promotions and salon updates" },
];

const PREFERENCE_STATE_OPTIONS = [
  { value: "", label: "Any preference state" },
  { value: "opted_in", label: "Opted in" },
  { value: "opted_out", label: "Opted out" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100, 250];

const QUICK_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "unsubscribed", label: "Unsubscribed" },
  { value: "hard_optout", label: "Hard opt-out" },
  { value: "subscribed", label: "Subscribed" },
  { value: "recently_changed", label: "Recently changed" },
  { value: "missing_preference_data", label: "Missing preference data" },
];

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // Fall back to local time.
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDaysToIsoDate = (dateStr, deltaDays) => {
  if (!dateStr) return "";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts.map((value) => Number(value));
  if (![year, month, day].every((value) => Number.isFinite(value))) return "";
  const base = new Date(Date.UTC(year, month - 1, day));
  base.setUTCDate(base.getUTCDate() + deltaDays);
  return formatDateISO(base, "UTC");
};

export default function EmailSubscriptionReport() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.EMAIL_SUBSCRIPTION;
  const salonId = getCurrentSalonId(user);
  const salonName = getCurrentSalonName(user);
  const salonTimeZone = user?.salon_timezone || "America/Toronto";

  const todayIso = useMemo(
    () => formatDateISO(new Date(), salonTimeZone),
    [salonTimeZone]
  );
  const defaultStart = useMemo(() => addDaysToIsoDate(todayIso, -30), [todayIso]);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(todayIso);
  const [segment, setSegment] = useState("");
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [preferences, setPreferences] = useState([]);
  const [batches, setBatches] = useState([]);
  const [clientRows, setClientRows] = useState([]);
  const [clientTotal, setClientTotal] = useState(0);
  const [preferenceCategory, setPreferenceCategory] = useState("");
  const [preferenceState, setPreferenceState] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [quickFilter, setQuickFilter] = useState("");
  const [clientPage, setClientPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const clientTotalPages = Math.max(1, Math.ceil(clientTotal / clientPageSize));
  const clientShowingStart = clientTotal > 0 ? (clientPage - 1) * clientPageSize + 1 : 0;
  const clientShowingEnd = clientTotal > 0 ? Math.min(clientPage * clientPageSize, clientTotal) : 0;

  const resetClientPage = () => setClientPage(1);

  const buildClientListPayload = useCallback(
    (overrides = {}) => ({
      salon_id: salonId,
      search: clientSearch.trim() || null,
      status: segment || "ALL",
      category: preferenceCategory || null,
      preference_state: preferenceState || null,
      quick_filter: quickFilter || null,
      page: clientPage,
      page_size: clientPageSize,
      ...overrides,
    }),
    [
      salonId,
      clientSearch,
      segment,
      preferenceCategory,
      preferenceState,
      quickFilter,
      clientPage,
      clientPageSize,
    ]
  );

  const runReport = useCallback(async () => {
    if (!salonId) return;
    setLoading(true);
    setError("");
    try {
      const payload = {
        salon_id: salonId,
        start_date: startDate,
        end_date: endDate,
        segment: segment || null,
      };
      const [
        summaryData,
        trendData,
        reasonData,
        preferenceData,
        batchData,
        clientData,
      ] = await Promise.all([
        getEmailSubscriptionSummary(payload, token),
        getEmailSubscriptionTrends(payload, token),
        getEmailSubscriptionReasons(payload, token),
        getEmailSubscriptionPreferences(payload, token),
        getEmailSubscriptionBatches(payload, token),
        searchEmailOptOutClients(
          buildClientListPayload(),
          token
        ),
      ]);
      setSummary(summaryData || null);
      setTrends(trendData?.points || []);
      setReasons(reasonData?.rows || []);
      setPreferences(preferenceData?.rows || []);
      setBatches(batchData?.rows || []);
      setClientRows(Array.isArray(clientData?.rows) ? clientData.rows : []);
      setClientTotal(Number(clientData?.total || 0));
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to run report.");
    } finally {
      setLoading(false);
    }
  }, [salonId, startDate, endDate, segment, token, buildClientListPayload]);

  useEffect(() => {
    if (salonId) {
      runReport();
    }
  }, [salonId, runReport]);

  useEffect(() => {
    if (clientPage > clientTotalPages) {
      setClientPage(clientTotalPages);
    }
  }, [clientPage, clientTotalPages]);

  const handleQuickFilterChange = (nextValue) => {
    setQuickFilter(nextValue);
    resetClientPage();
    if (!nextValue) {
      setSegment("");
    } else if (nextValue === "unsubscribed") {
      setSegment("UNSUBSCRIBED");
    } else if (nextValue === "hard_optout") {
      setSegment("HARD_OPTOUT");
    } else if (nextValue === "subscribed") {
      setSegment("SUBSCRIBED");
    } else {
      setSegment("");
    }
  };

  const handleExportCsv = async () => {
    if (!salonId) return;
    setExporting(true);
    setError("");
    try {
      const response = await exportEmailOptOutClients(
        buildClientListPayload({ page: 1, page_size: 250 }),
        token
      );
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const disposition = response.headers?.["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || "email_subscription_clients.csv";
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to export client list.");
    } finally {
      setExporting(false);
    }
  };

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate(getReportsHomePathForRoute(location.pathname), { replace: true });
  };

  return (
    <div className="reportSectionShell">
      <div className="reportSectionWide report-section-tight report-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Email Subscription Report
            </div>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div
              id="email-subscription-overview"
              className={getSectionClassName("email-subscription-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <div
              id="email-subscription-filters"
              className={`rounded-2xl border border-slate-200 bg-white p-4 space-y-3 ${getSectionClassName(
                "email-subscription-filters"
              )}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Report filters
                  </div>
                  <div className="text-xs text-slate-500">
                    Salon: {salonName || "Unknown"} (ID {salonId || "--"})
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate(addDaysToIsoDate(todayIso, -7));
                      setEndDate(todayIso);
                      resetClientPage();
                    }}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700"
                  >
                    Last 7 days
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate(addDaysToIsoDate(todayIso, -30));
                      setEndDate(todayIso);
                      resetClientPage();
                    }}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700"
                  >
                    Last 30 days
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate(addDaysToIsoDate(todayIso, -90));
                      setEndDate(todayIso);
                      resetClientPage();
                    }}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700"
                  >
                    Last 90 days
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate(addDaysToIsoDate(todayIso, -365));
                      setEndDate(todayIso);
                      resetClientPage();
                    }}
                    className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700"
                  >
                    Last 1 year
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">
                    Start date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      resetClientPage();
                    }}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">
                    End date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setEndDate(e.target.value);
                      resetClientPage();
                    }}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">
                    Segment
                  </label>
                  <select
                    value={segment}
                    onChange={(e) => {
                      setSegment(e.target.value);
                      setQuickFilter("");
                      resetClientPage();
                    }}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  >
                    <option value="">All segments</option>
                    <option value="SUBSCRIBED">Subscribed</option>
                    <option value="UNSUBSCRIBED">Unsubscribed</option>
                    <option value="NEVER_ASKED">Never Asked</option>
                    <option value="PENDING_OPT_IN">Pending Opt-In</option>
                    <option value="HARD_OPTOUT">Hard Opt-Out</option>
                    <option value="UNKNOWN">Unknown</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">
                    Preference
                  </label>
                  <select
                    value={preferenceCategory}
                    onChange={(e) => {
                      setPreferenceCategory(e.target.value);
                      setPreferenceState("");
                      resetClientPage();
                    }}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                  >
                    {PREFERENCE_OPTIONS.map((option) => (
                      <option key={option.value || "all"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">
                    Preference state
                  </label>
                  <select
                    value={preferenceState}
                    onChange={(e) => {
                      setPreferenceState(e.target.value);
                      resetClientPage();
                    }}
                    className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm"
                    disabled={!preferenceCategory}
                  >
                    {PREFERENCE_STATE_OPTIONS.map((option) => (
                      <option key={option.value || "any"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={runReport}
                    className="rounded-full bg-rose-500 px-4 py-2 text-xs font-semibold text-white"
                    disabled={loading}
                  >
                    {loading ? "Running..." : "Run report"}
                  </button>
                </div>
              </div>
            </div>

            <div
              id="email-subscription-kpis"
              className={`grid grid-cols-1 md:grid-cols-3 gap-4 ${getSectionClassName(
                "email-subscription-kpis"
              )}`}
            >
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">
                  Subscribed %
                </div>
                <div className="text-lg font-semibold text-slate-900">
                  {Math.round((summary?.subscribed_pct || 0) * 100)}%
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">
                  New subscribers
                </div>
                <div className="text-lg font-semibold text-slate-900">
                  {summary?.new_subscribers || 0}
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold uppercase text-slate-500">
                  Unsubscribes
                </div>
                <div className="text-lg font-semibold text-slate-900">
                  {summary?.unsubscribes || 0}
                </div>
              </div>
            </div>

            <div
              id="email-subscription-details"
              className={`rounded-2xl border border-slate-200 bg-white p-4 ${getSectionClassName(
                "email-subscription-details"
              )}`}
            >
              <div className="text-sm font-semibold text-slate-900">
                Trend (subscribes/unsubscribes)
              </div>
              <div className="mt-3 space-y-2">
                {trends.map((point) => (
                  <div key={point.date} className="flex items-center gap-3">
                    <div className="w-24 text-xs text-slate-500">{point.date}</div>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-2 bg-emerald-400"
                        style={{ width: `${Math.min(point.subscribed * 10, 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-slate-600">
                      +{point.subscribed}
                    </div>
                    <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-2 bg-rose-400"
                        style={{ width: `${Math.min(point.unsubscribed * 10, 100)}%` }}
                      />
                    </div>
                    <div className="text-xs text-slate-600">
                      -{point.unsubscribed}
                    </div>
                  </div>
                ))}
                {trends.length === 0 && (
                  <div className="text-xs text-slate-500">No trend data.</div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-sm font-semibold text-slate-900">
                  Unsubscribe reasons
                </div>
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Reason</th>
                        <th className="px-3 py-2 text-left">Count</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {reasons.map((row) => (
                        <tr key={row.reason}>
                          <td className="px-3 py-2 text-slate-700">{row.reason}</td>
                          <td className="px-3 py-2 text-slate-600">{row.count}</td>
                        </tr>
                      ))}
                      {reasons.length === 0 && (
                        <tr>
                          <td
                            colSpan="2"
                            className="px-3 py-6 text-center text-xs text-slate-500"
                          >
                            No unsubscribe reasons yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-sm font-semibold text-slate-900">
                  Content preferences
                </div>
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="px-3 py-2 text-left">Preference</th>
                        <th className="px-3 py-2 text-left">Opted in</th>
                        <th className="px-3 py-2 text-left">Opted out</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {preferences.map((row) => (
                        <tr key={row.preference}>
                          <td className="px-3 py-2 text-slate-700">
                            {row.preference.replace(/_/g, " ")}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.subscribed_count ?? "--"}
                          </td>
                          <td className="px-3 py-2 text-slate-600">
                            {row.unsubscribed_count ?? row.count}
                          </td>
                        </tr>
                      ))}
                      {preferences.length === 0 && (
                        <tr>
                          <td
                            colSpan="3"
                            className="px-3 py-6 text-center text-xs text-slate-500"
                          >
                            No preferences captured yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-sm font-semibold text-slate-900">
                Recent opt-in invite batches
              </div>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Sent</th>
                      <th className="px-3 py-2 text-left">Total sent</th>
                      <th className="px-3 py-2 text-left">Open rate</th>
                      <th className="px-3 py-2 text-left">Click rate</th>
                      <th className="px-3 py-2 text-left">Subscribe rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {batches.map((row) => (
                      <tr key={row.batch_id}>
                        <td className="px-3 py-2 text-slate-700">
                          {row.sent_at
                            ? formatDateTimeInTimeZone(row.sent_at, salonTimeZone)
                            : "--"}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {row.total_sent}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {Math.round((row.open_rate || 0) * 100)}%
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {Math.round((row.click_rate || 0) * 100)}%
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {Math.round((row.subscribe_rate || 0) * 100)}%
                        </td>
                      </tr>
                    ))}
                    {batches.length === 0 && (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-3 py-6 text-center text-xs text-slate-500"
                        >
                          No invite batches in this range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    Client list
                  </div>
                  <div className="text-xs text-slate-500">
                    Showing {clientShowingStart}-{clientShowingEnd} of {clientTotal} clients for the selected status and preference filters.
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                    Rows
                    <select
                      value={clientPageSize}
                      onChange={(event) => {
                        setClientPageSize(Number(event.target.value));
                        resetClientPage();
                      }}
                      className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-xs"
                    >
                      {PAGE_SIZE_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={handleExportCsv}
                    className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    disabled={exporting || loading}
                  >
                    {exporting ? "Exporting..." : "Export CSV"}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/manager/marketing/email-opt-outs")}
                    className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Manage Opt-Outs
                  </button>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label className="flex min-w-[240px] flex-1 items-center gap-2 text-xs font-semibold text-slate-600">
                  Search
                  <input
                    value={clientSearch}
                    onChange={(event) => {
                      setClientSearch(event.target.value);
                      resetClientPage();
                    }}
                    className="h-9 min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-xs font-normal"
                    placeholder="Client name or email"
                  />
                </label>
                {QUICK_FILTER_OPTIONS.map((option) => {
                  const active =
                    option.value === ""
                      ? quickFilter === "" && !segment
                      : quickFilter === option.value;
                  return (
                    <button
                      key={option.value || "all"}
                      type="button"
                      onClick={() => handleQuickFilterChange(option.value)}
                      className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${
                        active
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      <th className="px-3 py-2 text-left">Client</th>
                      <th className="px-3 py-2 text-left">Email</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {clientRows.map((row) => (
                      <tr key={row.client_id}>
                        <td className="px-3 py-2 text-slate-700">
                          {row.name || "Unnamed client"}
                        </td>
                        <td className="px-3 py-2 text-slate-600">{row.email}</td>
                        <td className="px-3 py-2 text-slate-600">
                          {String(row.status || "Unknown").replace(/_/g, " ")}
                        </td>
                        <td className="px-3 py-2 text-slate-600">
                          {row.unsubscribe_reason || "--"}
                        </td>
                      </tr>
                    ))}
                    {clientRows.length === 0 && (
                      <tr>
                        <td
                          colSpan="4"
                          className="px-3 py-6 text-center text-xs text-slate-500"
                        >
                          No clients match these filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 md:flex-row md:items-center md:justify-between">
                <div className="text-xs text-slate-500">
                  Showing {clientShowingStart}-{clientShowingEnd} of {clientTotal} clients
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setClientPage(1)}
                    disabled={loading || clientPage <= 1}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    First
                  </button>
                  <button
                    type="button"
                    onClick={() => setClientPage((current) => Math.max(1, current - 1))}
                    disabled={loading || clientPage <= 1}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-2 text-xs font-semibold text-slate-600">
                    Page {clientPage} of {clientTotalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setClientPage((current) => Math.min(clientTotalPages, current + 1))
                    }
                    disabled={loading || clientPage >= clientTotalPages}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Next
                  </button>
                  <button
                    type="button"
                    onClick={() => setClientPage(clientTotalPages)}
                    disabled={loading || clientPage >= clientTotalPages}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Last
                  </button>
                </div>
              </div>
            </div>

            <div
              id="email-subscription-export"
              className={`rounded-2xl border border-slate-200 bg-white p-4 text-sm text-slate-600 ${getSectionClassName(
                "email-subscription-export"
              )}`}
            >
              Use Export CSV in the Client list section to download the full filtered client list.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
